/**
 * Perfil sub-pages — v1.8.
 *
 * Provides the four dedicated screens the /perfil hub links to:
 *   /perfil/cuenta       → edit name/email/company/bio + avatar (avatar
 *                          also editable from the main /perfil screen)
 *   /perfil/seguridad    → change password (email change lives in /cuenta)
 *   /perfil/preferencias → reuses the theme/language toggles already
 *                          shipped inside Configuracion.tsx (no duplication)
 *   /perfil/soporte      → create a support ticket + see own tickets
 *
 * Each screen is short and self-contained; heavy lifting is delegated
 * to existing endpoints.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, Loader2, KeyRound, Send, Mail, User, Building, FileText, CheckCircle2, Palette, Layers } from 'lucide-react';
import { apiService } from '../services/api';
import { UserTopBar } from '../components/user/UserTopBar';
import { useAuth } from '../contexts/AuthContext';
import { useTheme, ACCENTS, BG_VARIANTS } from '../contexts/ThemeContext';
import { toast } from 'sonner';

// ─────────────────────────────────────────────────────────────
// /perfil/cuenta — Manage account (name, email, bio, company)
// ─────────────────────────────────────────────────────────────
export const CuentaPage: React.FC = () => {
  const nav = useNavigate();
  const auth = useAuth();
  const [me, setMe] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', bio: '', company: '' });

  const load = useCallback(async () => {
    const m = await apiService.getMe();
    setMe(m);
    setForm({ name: m.name || '', email: m.email || '', bio: m.bio || '', company: m.company || '' });
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const patch: any = {};
      for (const k of ['name', 'email', 'bio', 'company'] as const) {
        if ((form as any)[k] !== (me as any)?.[k]) patch[k] = (form as any)[k];
      }
      if (Object.keys(patch).length === 0) { toast.info('Nada que guardar.'); return; }
      const updated = await apiService.updateProfile(patch);
      setMe(updated);
      (auth as any).updateUser?.(updated);
      toast.success('Perfil actualizado.');
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'No se pudo guardar.');
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-slate-950 nx-circuit text-slate-100" data-testid="cuenta-page">
      <UserTopBar title="Mi cuenta" onBack={() => nav('/perfil')} avatarUrl={me?.avatar_url} />
      <main className="max-w-2xl mx-auto px-4 py-8 space-y-5">
        <Section>
          <Field icon={<User size={14} />} label="Nombre completo" testId="cuenta-name"
                 value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
          <Field icon={<Mail size={14} />} label="Correo electrónico" type="email" testId="cuenta-email"
                 value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
          <Field icon={<Building size={14} />} label="Empresa / Organización" testId="cuenta-company"
                 value={form.company} onChange={(v) => setForm({ ...form, company: v })} />
          <Field icon={<FileText size={14} />} label="Bio / Descripción" testId="cuenta-bio" as="textarea"
                 value={form.bio} onChange={(v) => setForm({ ...form, bio: v })} />
        </Section>
        <button
          onClick={save} disabled={saving}
          className="w-full inline-flex items-center justify-center gap-2 rounded-2xl px-5 py-3.5 text-sm font-semibold disabled:opacity-60"
          style={{ background: 'hsl(var(--accent))', color: 'hsl(210 40% 8%)' }}
          data-testid="cuenta-save"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          Guardar cambios
        </button>

        {/* v1.16 · point #4 — Personalización dentro de Mi Cuenta. */}
        <PersonalizacionSection />

        {/* v1.16 · point #3 — Sección Seguridad embebida (contraseña) para
            que el usuario nunca tenga que navegar a otra pantalla. */}
        <SeguridadInline />
      </main>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Personalización — background + accent color pickers
// ─────────────────────────────────────────────────────────────
const PersonalizacionSection: React.FC = () => {
  const { accent, bg, setAccent, setBg } = useTheme();
  return (
    <section className="nx-glass p-5 space-y-5" data-testid="personalizacion-section">
      <div className="flex items-center gap-2">
        <Palette size={16} style={{ color: 'hsl(var(--accent))' }} />
        <h2 className="text-sm font-semibold uppercase tracking-[0.2em]">Personalización</h2>
      </div>

      {/* Fondo */}
      <div>
        <div className="text-[11px] uppercase tracking-widest mb-2" style={{ color: 'hsl(var(--text-muted))' }}>
          <Layers size={11} className="inline mr-1 -mt-0.5" /> Fondo
        </div>
        <div className="grid grid-cols-2 gap-2">
          {BG_VARIANTS.map(v => (
            <button
              key={v.id}
              onClick={() => setBg(v.id)}
              data-testid={`bg-variant-${v.id}`}
              className="rounded-xl p-3 text-left transition"
              style={{
                background: v.hex,
                border: `1.5px solid ${bg === v.id ? 'hsl(var(--accent))' : 'rgba(255,255,255,0.08)'}`,
                boxShadow: bg === v.id ? '0 0 24px hsl(var(--accent) / 0.25)' : 'none',
              }}
            >
              <div className="text-xs font-medium text-white">{v.label}</div>
              <div className="text-[10px] mt-0.5" style={{ color: 'rgba(255,255,255,0.5)' }}>{v.hex}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Acento */}
      <div>
        <div className="text-[11px] uppercase tracking-widest mb-2" style={{ color: 'hsl(var(--text-muted))' }}>
          <Palette size={11} className="inline mr-1 -mt-0.5" /> Color de acento
        </div>
        <div className="grid grid-cols-3 gap-2">
          {ACCENTS.map(a => (
            <button
              key={a.id}
              onClick={() => setAccent(a.id)}
              data-testid={`accent-${a.id}`}
              className="rounded-xl p-3 text-left transition flex items-center gap-2"
              style={{
                background: 'hsl(var(--bg-elev) / 0.5)',
                border: `1.5px solid ${accent === a.id ? a.hex : 'rgba(255,255,255,0.06)'}`,
                boxShadow: accent === a.id ? `0 0 20px ${a.hex}55` : 'none',
              }}
            >
              <span
                className="w-4 h-4 rounded-full"
                style={{ background: a.hex, boxShadow: `0 0 10px ${a.hex}66` }}
                aria-hidden
              />
              <span className="text-xs font-medium truncate">{a.label}</span>
            </button>
          ))}
        </div>
        <div className="text-[10px] mt-2" style={{ color: 'hsl(var(--text-muted))' }}>
          Toda la aplicación cambia al instante — se aplica también a botones, halos y bordes.
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────
// SeguridadInline — cambio de contraseña embebido en Mi Cuenta
// ─────────────────────────────────────────────────────────────
const SeguridadInline: React.FC = () => {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (next.length < 8) { toast.error('Mínimo 8 caracteres.'); return; }
    if (next !== confirm) { toast.error('Las contraseñas no coinciden.'); return; }
    setBusy(true);
    try {
      await (apiService as any).changePassword?.(current, next);
      toast.success('Contraseña actualizada.');
      setCurrent(''); setNext(''); setConfirm('');
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'No se pudo actualizar la contraseña.');
    } finally { setBusy(false); }
  };
  return (
    <section className="nx-glass p-5" data-testid="seguridad-inline-section">
      <div className="flex items-center gap-2 mb-4">
        <KeyRound size={16} style={{ color: 'hsl(var(--accent))' }} />
        <h2 className="text-sm font-semibold uppercase tracking-[0.2em]">Seguridad</h2>
      </div>
      <form className="space-y-3" onSubmit={submit}>
        <Field icon={<KeyRound size={14} />} label="Contraseña actual" testId="pw-current" type="password"
               value={current} onChange={setCurrent} />
        <Field icon={<KeyRound size={14} />} label="Nueva contraseña" testId="pw-new" type="password"
               value={next} onChange={setNext} />
        <Field icon={<CheckCircle2 size={14} />} label="Confirmar" testId="pw-confirm" type="password"
               value={confirm} onChange={setConfirm} />
        <button
          type="submit" disabled={busy || !current || !next}
          className="w-full inline-flex items-center justify-center gap-2 rounded-2xl px-5 py-3 text-sm font-semibold disabled:opacity-60"
          style={{ background: 'hsl(var(--accent))', color: 'hsl(210 40% 8%)' }}
          data-testid="pw-submit"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />} Cambiar contraseña
        </button>
      </form>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────
// /perfil/seguridad — Change password
// ─────────────────────────────────────────────────────────────
export const SeguridadPage: React.FC = () => {
  const nav = useNavigate();
  const [me, setMe] = useState<any>(null);
  const [cur, setCur] = useState(''); const [next, setNext] = useState(''); const [conf, setConf] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { apiService.getMe().then(setMe).catch(() => setMe(null)); }, []);

  const save = async () => {
    if (next.length < 8) { toast.error('Mínimo 8 caracteres.'); return; }
    if (next !== conf) { toast.error('Las contraseñas no coinciden.'); return; }
    setSaving(true);
    try {
      await apiService.changePassword(cur, next);
      toast.success('Contraseña actualizada. Vuelve a iniciar sesión si te lo pide.');
      setCur(''); setNext(''); setConf('');
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'No se pudo cambiar.');
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-slate-950 nx-circuit text-slate-100" data-testid="seguridad-page">
      <UserTopBar title="Seguridad" onBack={() => nav('/perfil')} avatarUrl={me?.avatar_url} />
      <main className="max-w-2xl mx-auto px-4 py-8 space-y-5">
        <Section title="Cambiar contraseña" subtitle="Elige una contraseña de al menos 8 caracteres.">
          <Field icon={<KeyRound size={14} />} label="Contraseña actual" type="password" testId="seg-cur"
                 value={cur} onChange={setCur} />
          <Field icon={<KeyRound size={14} />} label="Nueva contraseña" type="password" testId="seg-next"
                 value={next} onChange={setNext} />
          <Field icon={<KeyRound size={14} />} label="Confirmar nueva contraseña" type="password" testId="seg-conf"
                 value={conf} onChange={setConf} />
        </Section>
        <button
          onClick={save} disabled={saving || !cur || !next || !conf}
          className="w-full inline-flex items-center justify-center gap-2 rounded-2xl px-5 py-3.5 text-sm font-semibold disabled:opacity-60"
          style={{ background: 'hsl(var(--accent))', color: 'hsl(210 40% 8%)' }}
          data-testid="seg-save"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          Actualizar contraseña
        </button>
      </main>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// /perfil/preferencias — Reuses existing Configuracion sections.
// The Configuracion page already ships language/theme/notifications
// toggles wired to the backend; we simply render it here inside the
// user-facing chrome (no duplicated logic).
// ─────────────────────────────────────────────────────────────
export const PreferenciasPage: React.FC = () => {
  const nav = useNavigate();
  const [me, setMe] = useState<any>(null);
  useEffect(() => { apiService.getMe().then(setMe).catch(() => setMe(null)); }, []);
  const ConfiguracionInner = React.lazy(() => import('./Configuracion'));
  return (
    <div className="min-h-screen bg-slate-950 nx-circuit text-slate-100" data-testid="preferencias-page">
      <UserTopBar title="Preferencias" onBack={() => nav('/perfil')} avatarUrl={me?.avatar_url} />
      <main className="max-w-3xl mx-auto px-4 py-6">
        <React.Suspense fallback={<div className="text-center text-slate-500 py-10">Cargando…</div>}>
          <ConfiguracionInner />
        </React.Suspense>
      </main>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// /perfil/soporte — Modern chat (WhatsApp/ChatGPT-style)
// Sprint Cierre de Base v1.26: el cliente ya no ve un formulario;
// simplemente escribe. El primer mensaje crea un ticket con asunto
// auto-derivado; los mensajes siguientes van al mismo hilo hasta que
// el admin lo cierra o el usuario abre otro con "Nueva conversación".
// ─────────────────────────────────────────────────────────────
export const SoporteUsuarioPage: React.FC = () => {
  const nav = useNavigate();
  const [me, setMe] = useState<any>(null);
  const [tickets, setTickets] = useState<any[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = React.useRef<HTMLDivElement>(null);

  const active = tickets.find((t) => t.id === activeId) || null;

  const reload = useCallback(async () => {
    try {
      const list = await apiService.listMyTickets();
      // Sprint Cierre Final v1.27 · Chat continuo: sólo UN hilo activo.
      // Si el usuario tiene un ticket "open" o "answered", ese es el
      // único que se muestra. Los cerrados se ignoran para el usuario
      // (el admin puede seguir viéndolos en su panel).
      const open = list.find((t: any) => t.status === 'open' || t.status === 'answered');
      setTickets(open ? [open] : []);
      setActiveId(open?.id || null);
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => {
    apiService.getMe().then(setMe).catch(() => setMe(null));
    reload();
  }, [reload]);

  useEffect(() => {
    // Poll every 15s for admin replies while the tab is open.
    const t = setInterval(reload, 15000);
    return () => clearInterval(t);
  }, [reload]);

  useEffect(() => {
    // Scroll to bottom whenever the active thread changes.
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [activeId, active?.replies?.length]);

  const send = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      if (!active) {
        // First message → create ticket. Subject = auto-derived (first 6 words).
        const words = text.split(' ').slice(0, 6).join(' ').slice(0, 60);
        const subject = words || 'Nueva conversación';
        const created = await apiService.createTicket(subject, text, 'general');
        setDraft('');
        setActiveId(created?.id || null);
        await reload();
      } else {
        await apiService.replyTicket?.(active.id, text);
        setDraft('');
        await reload();
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'No se pudo enviar el mensaje.');
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // Build the flat message list for the active ticket.
  const messages = React.useMemo(() => {
    if (!active) return [] as Array<{ id: string; role: 'user' | 'admin'; text: string; at: string }>;
    const first = {
      id: `first_${active.id}`,
      role: 'user' as const,
      text: active.message || active.subject || '',
      at: active.created_at,
    };
    const replies = (active.replies || []).map((r: any) => ({
      id: r.id,
      role: r.by_admin ? ('admin' as const) : ('user' as const),
      text: r.message || '',
      at: r.created_at || r.at,
    }));
    return [first, ...replies];
  }, [active]);

  return (
    <div
      className="min-h-[100dvh] flex flex-col"
      style={{ background: 'hsl(var(--bg))', color: 'hsl(var(--text))' }}
      data-testid="soporte-usuario-page"
    >
      <UserTopBar title="Soporte" onBack={() => nav('/perfil')} avatarUrl={me?.avatar_url} />

      <div className="flex-1 max-w-2xl w-full mx-auto flex flex-col px-3 sm:px-4 pt-4 pb-3 min-h-0">
        {/* Sprint Cierre Final v1.27 · Chat continuo: sin selector de
            hilos, sin asunto, sin categoría. Solo una conversación. */}

        {/* Messages */}
        <div ref={scrollRef}
             className="flex-1 overflow-y-auto rounded-2xl p-4 space-y-3"
             style={{ background: 'hsl(var(--bg-elev) / 0.4)', border: '1px solid hsl(var(--border))' }}
             data-testid="soporte-messages">
          {!active && messages.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-center gap-3 py-12">
              <div className="w-16 h-16 rounded-full grid place-items-center"
                   style={{
                     background: 'radial-gradient(closest-side, hsl(var(--accent) / 0.25), transparent 70%)',
                     border: '1px dashed hsl(var(--accent) / 0.4)',
                   }}>
                <Send size={22} style={{ color: 'hsl(var(--accent))' }} />
              </div>
              <div className="text-base font-medium">Escríbenos y te respondemos</div>
              <div className="text-xs max-w-xs" style={{ color: 'hsl(var(--text-muted))' }}>
                Un mensaje simple es suficiente. Recibirás respuesta desde soporte lo antes posible.
              </div>
            </div>
          )}
          {messages.map((m) => (
            <div key={m.id} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                 data-testid={`soporte-msg-${m.role}`}>
              <div className="max-w-[82%] rounded-2xl px-3.5 py-2.5"
                   style={{
                     background: m.role === 'user'
                       ? 'hsl(var(--accent))'
                       : 'hsl(var(--bg-elev))',
                     color: m.role === 'user' ? 'hsl(210 40% 8%)' : 'hsl(var(--text))',
                     border: m.role === 'user' ? 'none' : '1px solid hsl(var(--border))',
                     borderBottomRightRadius: m.role === 'user' ? '6px' : undefined,
                     borderBottomLeftRadius: m.role === 'admin' ? '6px' : undefined,
                   }}>
                <div className="text-[13.5px] leading-relaxed whitespace-pre-wrap">{m.text}</div>
                <div className="text-[10px] mt-1 opacity-60 text-right">
                  {new Date(m.at).toLocaleString('es-ES', {
                    hour: '2-digit', minute: '2-digit',
                    day: '2-digit', month: 'short',
                  })}
                </div>
              </div>
            </div>
          ))}
          {active && active.status === 'answered' && (
            <div className="text-center text-[11px] py-2" style={{ color: 'hsl(var(--text-muted))' }}>
              Te hemos respondido.
            </div>
          )}
        </div>

        {/* Input */}
        <div className="mt-3 flex items-end gap-2 rounded-2xl p-2"
             style={{ background: 'hsl(var(--bg-elev))', border: '1px solid hsl(var(--border))' }}>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Escribe un mensaje…"
            rows={1}
            className="flex-1 bg-transparent outline-none resize-none text-sm px-2 py-2 max-h-32"
            style={{ color: 'hsl(var(--text))' }}
            data-testid="soporte-input"
          />
          <button
            onClick={send}
            disabled={sending || !draft.trim()}
            className="w-10 h-10 rounded-xl grid place-items-center disabled:opacity-40"
            style={{ background: 'hsl(var(--accent))', color: 'hsl(210 40% 8%)' }}
            data-testid="soporte-send"
            aria-label="Enviar"
          >
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          </button>
        </div>
      </div>
    </div>
  );
};


// ─────────────────────────────────────────────────────────────
// Shared UI primitives
// ─────────────────────────────────────────────────────────────
const Section: React.FC<React.PropsWithChildren<{ title?: string; subtitle?: string }>> = ({ title, subtitle, children }) => (
  <div
    className="rounded-2xl p-5 space-y-4"
    style={{
      background: 'linear-gradient(150deg, hsl(var(--bg-elev) / 0.9), hsl(var(--bg-elev) / 0.5))',
      border: '1px solid hsl(var(--border))',
      backdropFilter: 'blur(14px)',
    }}
  >
    {title && (
      <div>
        <div className="text-sm font-medium">{title}</div>
        {subtitle && <div className="text-xs mt-0.5" style={{ color: 'hsl(var(--text-muted))' }}>{subtitle}</div>}
      </div>
    )}
    {children}
  </div>
);

const Field: React.FC<{
  label: string; testId: string; value: string; onChange: (v: string) => void;
  icon?: React.ReactNode; type?: string; as?: 'input' | 'textarea'; rows?: number;
}> = ({ label, testId, value, onChange, icon, type = 'text', as = 'input', rows }) => (
  <div>
    <label className="text-[10px] uppercase tracking-wider inline-flex items-center gap-1.5" style={{ color: 'hsl(var(--text-muted))' }}>
      {icon} {label}
    </label>
    {as === 'textarea' ? (
      <textarea
        value={value} rows={rows || 4}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-lg px-3 py-2 text-sm resize-y"
        style={{ background: 'hsl(var(--bg))', border: '1px solid hsl(var(--border))', color: 'hsl(var(--text))' }}
        data-testid={testId}
      />
    ) : (
      <input
        value={value} type={type}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-lg px-3 py-2 text-sm"
        style={{ background: 'hsl(var(--bg))', border: '1px solid hsl(var(--border))', color: 'hsl(var(--text))' }}
        data-testid={testId}
      />
    )}
  </div>
);

export const SuccessIcon: React.FC = () => (
  <CheckCircle2 size={64} style={{ color: 'hsl(var(--accent))' }} />
);
